import {
  InvokeChangeCartOrderItemQuantities,
  InvokeDeleteCartOrderItem,
  InvokeBulkDeleteCartOrderItem,
  InvokeRestoreCartOrderItem,
  InvokeBulkRestoreCartOrderItem,
  _UpdateCartOrderItems,
  UpdateCartOrderItems,
  CartOrderItemStatus,
  ShowErrorDialog,
} from "@/libs/cart/order/types";
import { cartOrderApi } from "@/libs/api/index";
import { RequestData, CartOrderRequestType } from "@/libs/api/cart/types";
import { dialogType } from "@/libs/cart/dialog/types";

/**
 * 多重送信防止のための押下可能判定
 * グローバル変数
 */
let loading = false;

/**
 * 注文データ更新のAPIクライアントを呼び出す
 *
 * @param api APIClient
 * @returns 注文データ更新処理の結果
 */
export const _updateCartOrderItems: _UpdateCartOrderItems = (api) => (
  mode,
  orderNum,
  items
) => {
  const item: RequestData = {
    mode,
    orderNum,
    ...items,
  };
  loading = true;
  return api
    .updateCartOrderItems(item)
    .then((i) => {
      return i.responseData;
    })
    .finally(() => {
      loading = false;
    });
};

/**
 * 公開関数
 */
export const updateCartOrderItems: UpdateCartOrderItems = _updateCartOrderItems(
  cartOrderApi
);

/**
 * 注文確定済み商品の数量変更する
 * @param findParentElement 直近の親要素を取得する関数
 * @param getInputValue 要素の値を取得する関数
 * @param setContentToElement 要素のコンテンツを更新する関数
 * @param makeOptions option要素の生成関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param updateCartOrderItems 注文データ更新関数
 * @param showErrorDialog エラーダイアログ表示関数
 * @param findChangedCartOrderItem フィルタリングした要素を返す関数
 * @param invokeDialogStepper ダイアログのステップ関数
 * @param itemSelector 対象商品セレクタ
 * @param orderNumberSelector 注文ID格納セレクタ
 * @param deleteSelector 非活性制御用の削除セレクタ
 */
export const invokeChangeCartOrderItemQuantities: InvokeChangeCartOrderItemQuantities = (
  findParentElement,
  getInputValue,
  setContentToElement,
  makeOptions,
  setAttribute,
  removeAttribute,
  updateCartOrderItems,
  showErrorDialog,
  findChangedCartOrderItem,
  invokeDialogStepper,
  itemSelector,
  orderNumberSelector,
  deleteSelector
) => async (event) => {
  if (loading) return;
  const target = (event as any).currentTarget;
  if (!target) return;
  const $item = findParentElement(target, itemSelector);
  if (!$item) return;
  setAttribute(target, "disabled", "disabled");
  setAttribute(deleteSelector, "disabled", "disabled");
  invokeDialogStepper(
    false,
    false,
    [],
    (event as any).data.menuYear,
    (event as any).data.menuNum,
    async () => {
      removeAttribute(target, "disabled");
      removeAttribute(deleteSelector, "disabled");
      const value = getInputValue(target);
      const orderNum = `${getInputValue(orderNumberSelector)}`;
      const itemCode = `${$item.data("itemCode")}`;
      const parentItemCode = `${$item.data("parentItemCode")}`;
      const { changeItems, errItems } = await updateCartOrderItems(
        CartOrderRequestType.Change,
        orderNum,
        {
          item1: `${parentItemCode}:${itemCode}:${value}`,
        }
      );
      showErrorDialog(errItems);
      if (!changeItems.length) return;
      const changedItemData = findChangedCartOrderItem(
        orderNum,
        itemCode,
        parentItemCode,
        changeItems
      );
      if (!changedItemData) return;
      (event as any).data.getCartInfo();
      (event as any).data.showBalloon(event);
      setContentToElement(
        target,
        makeOptions(
          changedItemData.limitQuantity,
          changedItemData.afterQuantity
        )
      );
      if (changedItemData.afterQuantity !== changedItemData.fixedQuantity)
        setAttribute($item, "data-item-status", CartOrderItemStatus.Changed);
    },
    () => {}
  );
};

/**
 * 注文確定済み商品を削除する
 * @param findParentElement 直近の親要素を取得する関数
 * @param getInputValue 要素の値を取得する関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param updateCartOrderItems 注文データ更新関数
 * @param showErrorDialog エラーダイアログ表示関数
 * @param findChangedCartOrderItem フィルタリングした要素を返す関数
 * @param toggleItemActionElements 商品更新アクション要素表示切り替え関数
 * @param invokeDialogStepper ダイアログのステップ関数
 * @param itemSelector 対象商品セレクタ
 * @param orderNumberSelector 注文ID格納セレクタ
 * @param selectSelector 非活性制御用の数量変更セレクタ
 * @param reservedClass 予約商品判別用クラス名
 * @param nameSelector 商品名取得用セレクタ
 */
export const invokeDeleteCartOrderItem: InvokeDeleteCartOrderItem = (
  findParentElement,
  getInputValue,
  setAttribute,
  removeAttribute,
  updateCartOrderItems,
  showErrorDialog,
  findChangedCartOrderItem,
  toggleItemActionElements,
  invokeDialogStepper,
  itemSelector,
  orderNumberSelector,
  selectSelector,
  reservedClass,
  nameSelector
) => async (event) => {
  if (loading) return;
  const target = (event as any).currentTarget;
  if (!target) return;
  const $item = findParentElement(target, itemSelector);
  if (!$item) return;
  setAttribute(target, "disabled", "disabled");
  setAttribute(selectSelector, "disabled", "disabled");
  const isReserved = $item.hasClass(reservedClass);
  const itemNames = isReserved ? [$item.find(nameSelector).text()] : [];
  invokeDialogStepper(
    isReserved,
    false,
    itemNames,
    (event as any).data.menuYear,
    (event as any).data.menuNum,
    async () => {
      removeAttribute(target, "disabled");
      removeAttribute(selectSelector, "disabled");
      const orderNum = `${getInputValue(orderNumberSelector)}`;
      const itemCode = `${$item.data("itemCode")}`;
      const parentItemCode = `${$item.data("parentItemCode")}`;
      const { changeItems, errItems } = await updateCartOrderItems(
        CartOrderRequestType.Delete,
        orderNum,
        {
          item1: `${parentItemCode}:${itemCode}:0`,
        }
      );
      showErrorDialog(errItems);
      if (!changeItems.length) return;
      const changedItemData = findChangedCartOrderItem(
        orderNum,
        itemCode,
        parentItemCode,
        changeItems
      );
      if (!changedItemData) return;
      (event as any).data.getCartInfo();
      (event as any).data.showBalloon(event);
      toggleItemActionElements(target, $item, true);
      setAttribute($item, "data-item-status", CartOrderItemStatus.Canceled);
    },
    async () => {
      removeAttribute(target, "disabled");
      removeAttribute(selectSelector, "disabled");
    }
  );
};

/**
 * 注文確定済み商品を元に戻す
 * @param findParentElement 直近の親要素を取得する関数
 * @param getInputValue 要素の値を取得する関数
 * @param setContentToElement 要素のコンテンツを更新する関数
 * @param makeOptions option要素の生成関数
 * @param setAttribute 属性を設定する関数
 * @param updateCartOrderItems 注文データ更新関数
 * @param showErrorDialog エラーダイアログ表示関数
 * @param findChangedCartOrderItem フィルタリングした要素を返す関数
 * @param toggleItemActionElements 商品更新アクション要素表示切り替え関数
 * @param itemSelector 対象商品セレクタ
 * @param orderNumberSelector 注文ID格納セレクタ
 * @param selectSelector 商品数量セレクタ
 */
export const invokeRestoreCartOrderItem: InvokeRestoreCartOrderItem = (
  findParentElement,
  getInputValue,
  setContentToElement,
  makeOptions,
  setAttribute,
  updateCartOrderItems,
  showErrorDialog,
  findChangedCartOrderItem,
  toggleItemActionElements,
  itemSelector,
  orderNumberSelector,
  selectSelector
) => async (event) => {
  if (loading) return;
  const target = (event as any).currentTarget;
  if (!target) return;
  const $item = findParentElement(target, itemSelector);
  if (!$item) return;
  const orderNum = `${getInputValue(orderNumberSelector)}`;
  const itemCode = `${$item.data("itemCode")}`;
  const parentItemCode = `${$item.data("parentItemCode")}`;
  const { changeItems, errItems } = await updateCartOrderItems(
    CartOrderRequestType.Restore,
    orderNum,
    {
      item1: `${parentItemCode}:${itemCode}:0`,
    }
  );
  showErrorDialog(errItems);
  if (!changeItems.length) return;
  const changedItemData = findChangedCartOrderItem(
    orderNum,
    itemCode,
    parentItemCode,
    changeItems
  );
  if (!changedItemData) return;
  (event as any).data.getCartInfo();
  toggleItemActionElements(target, $item, false);
  const $selectElement = $item.find(selectSelector);
  setContentToElement(
    $selectElement,
    makeOptions(changedItemData.limitQuantity, changedItemData.afterQuantity)
  );
  setAttribute($item, "data-item-status", CartOrderItemStatus.Default);
};

/**
 * 注文確定済み商品を一括削除する
 * @param filterCartOrderItem 直近の親要素を取得する関数
 * @param getInputValue 要素の値を取得する関数
 * @param updateCartOrderItems 注文データ更新関数
 * @param setAttribute 属性を設定する関数
 * @param removeAttribute 属性を削除する関数
 * @param showErrorDialog エラーダイアログ表示関数
 * @param toggleItemActionElements 商品更新アクション要素表示切り替え関数
 * @param invokeDialogStepper ダイアログのステップ関数
 * @param hideAllDialogs すべてのダイアログを閉じる関数
 * @param omit 特定のプロパティを削除する関数
 * @param itemSelector 対象商品セレクタ
 * @param orderNumberSelector 注文ID格納セレクタ
 * @param deleteSelector 削除セレクタ
 * @param reservedClass 予約商品判別用クラス名
 * @param nameSelector 商品名取得用セレクタ
 * @param selector 一括対象範囲（商品エリアセレクタ）
 * @param filterExpr フィルタリング条件
 */
export const invokeBulkDeleteCartOrderItem: InvokeBulkDeleteCartOrderItem = (
  filterCartOrderItem,
  getInputValue,
  updateCartOrderItems,
  setAttribute,
  removeAttribute,
  showErrorDialog,
  toggleItemActionElements,
  invokeDialogStepper,
  hideAllDialogs,
  omit,
  itemSelector,
  orderNumberSelector,
  deleteSelector,
  reservedClass,
  nameSelector,
  selector,
  filterExpr
) => async (event) => {
  if (loading) return;
  const target = (event as any).currentTarget;
  if (!target) return;
  hideAllDialogs();
  const orderNum = `${getInputValue(orderNumberSelector)}`;
  const $items = filterCartOrderItem(selector, itemSelector, filterExpr);
  const params: Record<string, string> = {};
  const itemNames: string[] = [];
  const reservedItemKeys: string[] = [];
  let hasReserved = false;
  $items.map((i, x) => {
    const $item = $(x);
    const isReserved = $item.hasClass(reservedClass);
    const key = `item${i + 1}`;
    const itemCode = `${$item.data("itemCode")}`;
    const parentItemCode = `${$item.data("parentItemCode")}`;
    params[key] = `${parentItemCode}:${itemCode}:0`;
    if (isReserved) {
      hasReserved = true;
      itemNames.push($item.find(nameSelector).text());
      reservedItemKeys.push(key);
    }
  });
  setAttribute(target, "disabled", "disabled");
  invokeDialogStepper(
    hasReserved,
    true,
    itemNames,
    (event as any).data.menuYear,
    (event as any).data.menuNum,
    async () => {
      removeAttribute(target, "disabled");
      const { changeItems, errItems } = await updateCartOrderItems(
        CartOrderRequestType.Delete,
        orderNum,
        params
      );
      showErrorDialog(errItems);
      if (!changeItems.length) return;
      (event as any).data.getCartInfo();
      (event as any).data.showBalloon(event);
      changeItems.map((item) => {
        const $item = $(itemSelector).filter(
          (_, x) =>
            `${$(x).data("parentItemCode")}` === item.oyaItemCode &&
            `${$(x).data("itemCode")}` === item.itemCode
        );
        const $deleteSelector = $item.find(deleteSelector);
        toggleItemActionElements($deleteSelector.get(0), $item, true);
        setAttribute($item, "data-item-status", CartOrderItemStatus.Canceled);
      });
    },
    async () => {
      removeAttribute(target, "disabled");
      // NOTE: 予約商品をパラメーターから取り除く
      const filterdParams = omit(params, reservedItemKeys);
      const { changeItems, errItems } = await updateCartOrderItems(
        CartOrderRequestType.Delete,
        orderNum,
        filterdParams
      );
      showErrorDialog(errItems);
      if (!changeItems.length) return;
      (event as any).data.getCartInfo();
      (event as any).data.showBalloon(event);
      changeItems.map((item) => {
        const $item = $(itemSelector).filter(
          (_, x) =>
            `${$(x).data("parentItemCode")}` === item.oyaItemCode &&
            `${$(x).data("itemCode")}` === item.itemCode
        );
        const $deleteSelector = $item.find(deleteSelector);
        toggleItemActionElements($deleteSelector.get(0), $item, true);
        setAttribute($item, "data-item-status", CartOrderItemStatus.Canceled);
      });
    }
  );
};

/**
 * 注文確定済み商品を一括で元に戻す
 * @param filterCartOrderItem 直近の親要素を取得する関数
 * @param getInputValue 要素の値を取得する関数
 * @param setContentToElement 要素のコンテンツを更新する関数
 * @param makeOptions option要素の生成関数
 * @param updateCartOrderItems 注文データ更新関数
 * @param setAttribute 属性を設定する関数
 * @param showErrorDialog エラーダイアログ表示関数
 * @param toggleItemActionElements 商品更新アクション要素表示切り替え関数
 * @param hideAllDialogs すべてのダイアログを閉じる関数
 * @param itemSelector 対象商品セレクタ
 * @param orderNumberSelector 注文ID格納セレクタ
 * @param restoreSelector 元に戻すセレクタ
 * @param selector 一括対象範囲（商品エリアセレクタ）
 * @param filterExpr フィルタリング条件
 * @param selectSelector 商品数量セレクタ
 */
export const invokeBulkRestoreCartOrderItem: InvokeBulkRestoreCartOrderItem = (
  filterCartOrderItem,
  getInputValue,
  setContentToElement,
  makeOptions,
  updateCartOrderItems,
  setAttribute,
  showErrorDialog,
  toggleItemActionElements,
  hideAllDialogs,
  itemSelector,
  orderNumberSelector,
  restoreSelector,
  selector,
  filterExpr,
  selectSelector
) => async (event) => {
  if (loading) return;
  const target = (event as any).currentTarget;
  if (!target) return;
  hideAllDialogs();
  const orderNum = `${getInputValue(orderNumberSelector)}`;
  const $items = filterCartOrderItem(selector, itemSelector, filterExpr);
  const params: Record<string, string> = {};
  $items.map((i, x) => {
    const key = `item${i + 1}`;
    const itemCode = `${$(x).data("itemCode")}`;
    const parentItemCode = `${$(x).data("parentItemCode")}`;
    params[key] = `${parentItemCode}:${itemCode}:0`;
  });
  const { changeItems, errItems } = await updateCartOrderItems(
    CartOrderRequestType.Restore,
    orderNum,
    params
  );
  showErrorDialog(errItems);
  if (!changeItems.length) return;
  (event as any).data.getCartInfo();
  changeItems.map((item) => {
    const $item = $(itemSelector).filter(
      (_, x) =>
        `${$(x).data("parentItemCode")}` === item.oyaItemCode &&
        `${$(x).data("itemCode")}` === item.itemCode
    );
    const $restoreSelector = $item.find(restoreSelector);
    toggleItemActionElements($restoreSelector.get(0), $item, false);
    const $selectElement = $item.find(selectSelector);
    setContentToElement(
      $selectElement,
      makeOptions(item.limitQuantity, item.afterQuantity)
    );
    setAttribute($item, "data-item-status", CartOrderItemStatus.Default);
  });
};

/**
 * エラーダイアログを表示する
 * @param showDialog
 * @param setAttribute
 * @param removeAttribute
 * @param itemSelector
 * @param selectSelector
 * @param restoreSelector
 * @param deleteSelector
 */
export const showErrorDialog: ShowErrorDialog = (
  showDialog,
  setAttribute,
  removeAttribute,
  itemSelector,
  selectSelector,
  restoreSelector,
  deleteSelector
) => (errItems) => {
  if (!errItems.length) return;
  errItems.map((item) => {
    if (item.statusText !== "") {
      const $item = $(itemSelector).filter(
        (_, x) =>
          `${$(x).data("parentItemCode")}` === item.oyaItemCode &&
          `${$(x).data("itemCode")}` === item.itemCode
      );
      const $select = $item.find(selectSelector);
      if (!$select.prop("disabled")) $select.val(item.quantity);
      const $delete = $item.find(restoreSelector);
      const $restore = $item.find(deleteSelector);
      setAttribute($select, "disabled", "disabled");
      setAttribute($delete, "disabled", "disabled");
      setAttribute($restore, "disabled", "disabled");
      showDialog(
        item.statusText,
        dialogType.error,
        () => {
          removeAttribute($select, "disabled");
          removeAttribute($delete, "disabled");
          removeAttribute($restore, "disabled");
        },
        () => {}
      );
    }
  });
};
